import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";
import React, { Component } from "react";
import { v4 as uuid } from "uuid";

import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import { duration } from "@formant/core/model/duration";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IFilter } from "@formant/core/model/IFilter";
import { ITeleopViewConfiguration } from "@formant/core/model/ITeleopViewConfiguration";
import { ModuleMode } from "@formant/core/model/ModuleMode";
import {
    rosTypeCompressedImage,
    rosTypeJointState,
    rosTypeVideoFrame,
} from "@formant/core/model/rosTypes";
import {
    rtcCompressedImageStreamType,
    rtcH264VideoFrameStreamType,
    rtcJointStateStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { telemetryPipelineType } from "@formant/rtc-client/models/IStreamControl";
import { Icon } from "@formant/ui/component/common/Icon";

import { track } from "../../../analytics";
import {
    authStore,
    customModuleStore,
    metadataStore,
    observeStore,
    operateStore,
    viewStore,
} from "../../../stores";
import { ITeleopSession } from "../../../teleop/ITeleopSession";
import { createCustomWebViewUrl } from "../../../utils/createCustomWebViewUrl";
import { Timer } from "../../common/Timer";
import { CustomView } from "../../CustomView";
import { Lazy } from "../../Lazy";
import { GridModule, IGridModuleProps } from "../../Observe/GridModule";
import { generateStreamInfo } from "../../Observe/Modules";
import { TeleopScreen } from "../TeleopModule/TeleopScreen";
import { TeleopMouseInterceptor } from "../TeleopMouseInterceptor";
import * as styles from "./index.scss";

const TeleopMinimap = Lazy(
    async () => (await import("../TeleopModule/TeleopMinimap")).TeleopMinimap,
);

const TeleopBytestreamPlayer = Lazy(
    async () =>
        (await import("../video/TeleopBytestreamPlayer"))
            .TeleopBytestreamPlayer,
    true,
);

const UrdfViewer = Lazy(
    async () => (await import("../../UrdfViewer")).UrdfViewer,
    true,
);

export interface ITeleopViewProps {
    session: ITeleopSession;
    index: number;
    device?: IDevice;
    document?: IDeviceConfigurationDocument;
    viewConfig?: ITeleopViewConfiguration;
    className?: string;
    onFullscreen: (index: number) => void;
    onChangeStream?: (
        index: number,
        viewConfig: ITeleopViewConfiguration,
    ) => void;
    secondPrimary?: boolean;
}

@observer
export class TeleopView extends Component<ITeleopViewProps> {
    @observable
    private focusedStream?: [string, AddableModuleType?];

    @observable
    private authToken: string | undefined;

    private boundsRef = React.createRef<HTMLElement>();

    @observable
    private bytestreamPlayerKey = uuid();

    public componentDidMount() {
        authStore
            .token()
            .then(_ => (this.authToken = _))
            .catch(err => console.error(err));

        const { index, viewConfig, session } = this.props;

        track("Teleop View Visit", {
            DeviceId: session.deviceId,
            StreamName: viewConfig?.streamName,
        });

        if (index === 0 || index === 1) {
            const streamName = viewConfig?.streamName;
            const isCustomModule = streamName?.startsWith("module:");

            if (isCustomModule) {
                operateStore.fill = false;
                operateStore.canFill = false;
            } else {
                operateStore.canFill = true;
            }
        }
    }

    public render() {
        const {
            onToggleFullscreen,
            onFocus,
            onBlur,
            focusedStream,
            authToken,
            bytestreamPlayerKey,
            onContextLost,
        } = this;
        const {
            session,
            viewConfig,
            className,
            document,
            index,
            device,
            secondPrimary,
        } = this.props;

        const streamName = viewConfig?.streamName;
        const { shouldAutoFill } = operateStore;

        const teleopVideoStream = this.isTeleopVideoStream();
        const teleopCompressedImageStream =
            this.isTeleopCompressedImageStream();
        const teleopJointStateStream = this.isTeleopJointStateStream();
        const rtc =
            teleopVideoStream ||
            teleopCompressedImageStream ||
            teleopJointStateStream;
        const fullscreen = index === 0 || index === 1;

        const names = streamName ? [streamName] : [];

        const telemetryModuleFilter: IFilter = {
            deviceIds: [session.deviceId],
            names,
        };
        const metadata = metadataStore.list(telemetryModuleFilter);
        const streamInfo = generateStreamInfo(metadata || []);

        const videoStreamTypeMap: Record<string, string[]> = {};

        streamInfo.forEach(stream => {
            const { name, type } = stream;
            if (!videoStreamTypeMap[type]) {
                videoStreamTypeMap[type] = [];
            }
            videoStreamTypeMap[type].push(name);
        });

        const telemetryModule =
            !rtc &&
            (
                streamInfo.filter(({ type }) => type !== "localization") || []
            ).map(({ name, type }) => {
                const focused = name === focusedStream?.[0];
                const moduleProps: IGridModuleProps = {
                    streamName: name,
                    streamNames: videoStreamTypeMap[type],
                    streamType: type,
                    filter: { deviceIds: [session.deviceId], names: [name] },
                    noGrid: true,
                    teleop: true,
                    topTeleop: index === 0 && secondPrimary,
                    fullscreen: fullscreen && !secondPrimary,
                    configuration: [],
                    focused,
                    hideDevice: true,
                    device,
                    onToggleFullscreen,
                    onFocus,
                    onBlur,
                    moduleMode: ModuleMode.Observe,
                };

                session.controlRemoteStream({
                    streamName: name,
                    enablePriorityUpload: true,
                    pipeline: telemetryPipelineType,
                });

                return <GridModule key={name + index} {...moduleProps} />;
            })[0];

        const minimap = streamInfo.map(({ name, type }) => {
            if (type === "localization" && document && device?.id) {
                session.controlRemoteStream({
                    streamName: name,
                    enablePriorityUpload: true,
                    pipeline: telemetryPipelineType,
                });
                return (
                    <TeleopMinimap
                        key={name + index}
                        session={session}
                        document={document}
                        streamName={name}
                        mapNavigationConfigured={true}
                        fullscreen={fullscreen && !secondPrimary}
                        deviceId={device.id}
                    />
                );
            }
            return undefined;
        })[0];

        const customWebView =
            streamName?.startsWith("custom-view:") &&
            viewStore
                .list()
                ?.find(
                    _ =>
                        _.showOnTeleop &&
                        _.id === streamName.replace("custom-view:", ""),
                );

        const customModule =
            streamName?.startsWith("module:") &&
            customModuleStore
                .listAll()
                ?.find(_ => _.id === streamName.replace("module:", ""));

        if (
            !streamName ||
            (streamName.startsWith("custom-view:") && !customWebView)
        ) {
            return false;
        }

        let fill = secondPrimary
            ? false
            : operateStore.fill !== undefined
              ? operateStore.fill
              : shouldAutoFill;

        if (index === 0 && streamName?.startsWith("module:")) {
            fill = false;
        }

        const topPrimary = index === 0 && secondPrimary;
        const bottomPrimary = index === 1 && secondPrimary;

        return (
            <div
                className={classnames(
                    styles["teleop-view"],
                    !telemetryModule && styles["teleop-module"],
                    telemetryModule && styles["telemetry-module"],
                    teleopJointStateStream && styles.urdf,
                    fullscreen && styles.fullscreen,
                    topPrimary && styles.top,
                    bottomPrimary && styles.bottom,
                    fill ? styles.fill : styles.fit,
                    className,
                )}
            >
                {(!fullscreen || !fill) && !telemetryModule && (
                    <div
                        className={styles.header}
                        onDoubleClick={onToggleFullscreen}
                    >
                        {teleopVideoStream && <Icon name="video" />}
                        {teleopCompressedImageStream && <Icon name="image" />}
                        {minimap && <Icon name="localization" />}
                        <div className={styles.name}>
                            {customModule
                                ? customModule.name
                                : customWebView
                                  ? customWebView.name
                                  : streamName}
                        </div>
                    </div>
                )}
                {teleopVideoStream && (
                    <TeleopMouseInterceptor
                        boundsRef={this.boundsRef}
                        document={document}
                        session={session}
                        target={streamName}
                        primary={fullscreen}
                    >
                        <TeleopBytestreamPlayer
                            boundsRef={this.boundsRef}
                            session={session}
                            stream={{
                                entityId: session.deviceId,
                                streamName,
                                streamType: rtcH264VideoFrameStreamType,
                            }}
                            primary={fullscreen}
                            key={bytestreamPlayerKey}
                            onContextLost={onContextLost}
                            secondPrimary={secondPrimary}
                        />
                    </TeleopMouseInterceptor>
                )}
                {teleopCompressedImageStream && (
                    <TeleopMouseInterceptor
                        boundsRef={this.boundsRef}
                        document={document}
                        session={session}
                        target={streamName}
                        primary={fullscreen}
                    >
                        <TeleopScreen
                            boundsRef={this.boundsRef}
                            session={session}
                            primary={fullscreen}
                            stream={{
                                entityId: session.deviceId,
                                streamName,
                                streamType: rtcCompressedImageStreamType,
                            }}
                        />
                    </TeleopMouseInterceptor>
                )}
                {teleopJointStateStream && (
                    <UrdfViewer
                        streamName={streamName}
                        session={session}
                        document={document}
                    />
                )}
                {minimap}
                {telemetryModule && (
                    <div className={styles.content}>{telemetryModule}</div>
                )}
                {customWebView && authToken && (
                    <CustomView
                        src={createCustomWebViewUrl(
                            (customWebView.url ?? "") || "",
                            false,
                            device?.id,
                            authToken,
                            undefined,
                            undefined,
                            undefined,
                        )}
                        className={styles["custom-view"]}
                    />
                )}
                {customModule && authToken && (
                    <CustomView
                        src={createCustomWebViewUrl(
                            (customModule.url ?? "") || "",
                            true,
                            device?.id,
                            authToken,
                            undefined,
                            undefined,
                            undefined,
                            viewConfig.configurationId,
                        )}
                        className={styles["custom-view"]}
                    />
                )}
                <Timer onTick={this.onTick} interval={duration.second * 0.2} />
            </div>
        );
    }

    private onTick = () => {
        observeStore.time = new Date();
    };

    private onContextLost = () => {
        this.bytestreamPlayerKey = uuid();
    };

    private onToggleFullscreen = () => {
        const { index, onFullscreen, viewConfig } = this.props;
        const streamName = viewConfig?.streamName;
        const isCustomModule = streamName?.startsWith("module:");

        if (index !== 0 && index !== 1) {
            operateStore.canFill = !isCustomModule;
            onFullscreen(index);
        }
    };

    private onFocus = (stream?: [string, AddableModuleType?]) => {
        this.focusedStream = stream;
    };

    private onBlur = () => {
        this.focusedStream = undefined;
    };

    private isTeleopVideoStream(): boolean {
        const { document, viewConfig } = this.props;
        const streamName = viewConfig?.streamName;

        const isRosVideoStream = (document?.teleop?.rosStreams || []).some(
            ({ topicName, topicType, encodeVideo }) =>
                topicName === streamName &&
                (topicType === rosTypeVideoFrame ||
                    (topicType === rosTypeCompressedImage && !!encodeVideo)),
        );

        const isCustomVideoStream = (
            document?.teleop?.customStreams || []
        ).some(
            ({ name, rtcStreamType, encodeVideo }) =>
                name === streamName &&
                rtcStreamType === rtcH264VideoFrameStreamType &&
                !!encodeVideo,
        );
        const isHardwareVideoStream = (
            document?.teleop?.hardwareStreams || []
        ).some(
            ({ name, rtcStreamType }) =>
                name === streamName &&
                rtcStreamType === rtcH264VideoFrameStreamType,
        );

        return isRosVideoStream || isCustomVideoStream || isHardwareVideoStream;
    }

    private isTeleopCompressedImageStream(): boolean {
        const { document, viewConfig } = this.props;
        const streamName = viewConfig?.streamName;

        const isRosCompressedImageStream = (
            document?.teleop?.rosStreams || []
        ).some(
            ({ topicName, topicType, encodeVideo }) =>
                topicName === streamName &&
                topicType === rosTypeCompressedImage &&
                !encodeVideo,
        );

        const isCustomCompressedImageStream = (
            document?.teleop?.customStreams || []
        ).some(
            ({ name, rtcStreamType, encodeVideo }) =>
                name === streamName &&
                rtcStreamType === rtcCompressedImageStreamType &&
                !encodeVideo,
        );

        return isRosCompressedImageStream || isCustomCompressedImageStream;
    }

    private isTeleopJointStateStream(): boolean {
        const { document, viewConfig } = this.props;
        const streamName = viewConfig?.streamName;

        return (
            (document?.teleop?.rosStreams || []).some(
                ({ topicName, topicType }) =>
                    topicName === streamName && topicType === rosTypeJointState,
            ) ||
            (document?.teleop?.customStreams || []).some(
                ({ name, rtcStreamType }) =>
                    name === streamName &&
                    rtcStreamType === rtcJointStateStreamType,
            )
        );
    }
}
